<template>
  <div class="new-hearing-dialog">
    <el-form :model="form" label-position="top" ref="formRef" class="hearing-form" :rules="rules">
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="开始时间" prop="hearingTime" :rules="[{ required: true, message: '请选择开始时间', trigger: 'change' }]">
            <el-date-picker
                v-model="form.hearingTime"
                type="datetime"
                :picker-options="datePickerOptions"
                :shortcuts="shortcuts"
                format="yyyy-MM-dd HH:mm"
                value-format="yyyy-MM-dd HH:mm:ss"
                placeholder="请选择开始时间"
                style="width: 100%;"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="时长" prop="duration" :rules="[{ required: true, message: '请设置时长', trigger: 'change' }]">
            <el-input-number v-model="form.duration" :min="1" :max="8"></el-input-number>
            <span style="margin-left: 10px;">小时</span>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="会议类型" prop="hearingType" :rules="[{ required: true, message: '请选择会议类型', trigger: 'change' }]">
            <el-select v-model="form.hearingType" placeholder="请选择会议类型" style="width: 100%;">
              <el-option
                v-for="item in hearingTypeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="线上执法室" prop="courtId" :rules="[{ required: true, message: '请选择线上执法室', trigger: 'change' }]">
            <el-select v-model="form.courtId" placeholder="请选择线上执法室" style="width: 100%;">
              <el-option v-for="item in courtOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item>
        <el-checkbox v-model="form.sendSms">短信送达通知</el-checkbox>
      </el-form-item>

      <!-- 短信通知区域，勾选后显示 -->
      <div v-if="form.sendSms" class="notification-section">
        <!-- 所有参会人信息统一显示 -->
        <div v-if="allParticipants.length > 0" class="participant-section">
          <div class="section-title">参会人信息：</div>
          <div v-for="participant in allParticipants" :key="participant.id" class="info-text">
            <span class="identity">{{ participant.displayName }}</span>
            <span class="name">{{ participant.name }}</span>
            <span v-if="participant.meetingCode && participant.meetingCode !== 'xxxx'" class="meeting-code">会议号: {{ participant.meetingCode }}</span>
          </div>
        </div>
        
        <!-- 如果没有任何参会人信息 -->
        <div v-else class="info-text">
          暂无参会人信息，请先添加当事人或代理人
        </div>
      </div>

      <el-form-item class="action-buttons">
        <el-button @click="handleCancel" size="medium">取消</el-button>
        <el-button type="primary" @click="handleSave" size="medium" :loading="saving">{{ isEdit ? '更新' : '保存' }}</el-button>
      </el-form-item>
    </el-form>
  </div>
</template>

<script>
import request from '@/utils/request' // 引入axios封装
import {
  HEARING_TYPE_INVESTIGATION,
  ENFORCEMENT_HEARING_TYPE_OPTIONS,
} from '@/constants/enforcement'

export default {
  name: 'NewHearing',
  // 接收父组件传递的参数
  props: {
    // 调用场景：'case-list'（从案件列表调用）| 'case-edit'（从案件编辑页调用）
    context: {
      type: String,
      default: 'case-list',
      validator: value => ['case-list', 'case-edit'].includes(value)
    },
    // 案件ID
    caseId: {
      type: String,
      required: true
    },
    // 庭审ID（编辑模式需要）
    hearingId: {
      type: String,
      default: ''
    },
    // 是否为编辑模式
    isEdit: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const now = new Date();
    // 默认时间为当前时间的下一个整点
    now.setHours(now.getHours() + 1);
    now.setMinutes(0);
    now.setSeconds(0);
    const defaultTime = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:00:00`;

    return {
      isComponentMounted: true, // 添加组件挂载状态标志
      submitDate: '', // 案件受理日期（或当前日期）
      hearingTypeOptions: ENFORCEMENT_HEARING_TYPE_OPTIONS,
      form: {
        hearingTime: defaultTime,
        duration: 1,
        hearingType: HEARING_TYPE_INVESTIGATION,
        courtId: '', // 虚拟仲裁庭ID，动态设置默认值
        sendSms: true, // 默认勾选短信通知
      },
      courtOptions: [], // 虚拟仲裁庭选项，通过接口动态获取
      saving: false, // 保存按钮loading状态
      shortcuts: [
        {
          text: '明天上午9点',
          onClick(picker) {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            d.setHours(9, 0, 0, 0);
            picker.$emit('pick', d);
          }
        },
        {
          text: '下周一上午9点',
          onClick(picker) {
            const d = new Date();
            const day = d.getDay();
            const diff = (8 - day) % 7 || 7;
            d.setDate(d.getDate() + diff);
            d.setHours(9, 0, 0, 0);
            picker.$emit('pick', d);
          }
        }
      ],
      partyList: [], // 当事人列表
      agentList: [], // 代理人列表
      arbitratorList: [], // 仲裁员列表
      clerkList: [], // 书记员列表
      rules: {
        hearingTime: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
        duration: [{ required: true, message: '请设置时长', trigger: 'change' }],
        courtId: [{ required: true, message: '请选择线上执法室', trigger: 'change' }]
      }
    };
  },
  watch: {
    // 监听编辑模式和庭审ID的变化
    isEdit: {
      handler(newVal) {
        if (newVal && this.hearingId) {
          // 切换到编辑模式且有庭审ID时，重新加载数据
          console.log('检测到切换为编辑模式，庭审ID:', this.hearingId);
          this.loadExistingHearingData();
        }
      },
      immediate: false
    },
    hearingId: {
      handler(newVal, oldVal) {
        // 当庭审ID变化且处于编辑模式时，重新加载数据
        if (newVal && newVal !== oldVal && this.isEdit) {
          console.log('检测到庭审ID变化:', oldVal, '->', newVal);
          this.loadExistingHearingData();
        }
      },
      immediate: false
    }
  },
  computed: {
    // 是否为案件列表场景
    isCaseListContext() {
      return this.context === 'case-list';
    },
    
    // 是否为案件编辑场景
    isCaseEditContext() {
      return this.context === 'case-edit';
    },
    
    // 是否需要显示会议号（编辑模式下显示）
    shouldShowMeetingCode() {
      return this.isEdit && this.isCaseEditContext;
    },
    
    formattedHearingTime() {
      if (!this.form.hearingTime) return '';
      const date = new Date(this.form.hearingTime);
      return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 ${date.getHours()}点${String(date.getMinutes()).padStart(2, '0')}分`;
    },
    // 日期选择器的禁用规则，只允许选择晚于submitDate的日期
    datePickerOptions() {
      const minDate = this.submitDate ? new Date(this.submitDate) : new Date();
      return {
        disabledDate: (date) => {
          // 只能选择晚于提交日期的时间
          return date.getTime() < minDate.getTime();
        }
      };
    },
    // 合并所有参会人信息
    allParticipants() {
      const participants = [];
      
      // 添加当事人
      this.partyList.forEach(party => {
        participants.push({
          id: `party_${party.partyId || party.id}`,
          displayName: `${party.displayName || (party.partyIdentity === 1 ? '申请人' : '被申请人')}`,
          name: party.partyName || party.name,
          meetingCode: this.shouldShowMeetingCode ? (party.meetingCode || party.meetingNumber || party.meeting_number || 'xxxx') : undefined
        });
      });
      
      // 添加代理人
      this.agentList.forEach(agent => {
        participants.push({
          id: `agent_${agent.agentId || agent.id}`,
          displayName: `代理人`,
          name: agent.agentName || agent.name,
          meetingCode: this.shouldShowMeetingCode ? (agent.meetingCode || agent.meetingNumber || agent.meeting_number || 'xxxx') : undefined
        });
      });
      
      // 添加仲裁员
      this.arbitratorList.forEach(arbitrator => {
        participants.push({
          id: `arbitrator_${arbitrator.arbitratorId || arbitrator.id}`,
          displayName: `执法员`,
          name: arbitrator.arbitratorName || arbitrator.name,
          meetingCode: this.shouldShowMeetingCode ? (arbitrator.meetingCode || arbitrator.meetingNumber || arbitrator.meeting_number || 'xxxx') : undefined
        });
      });
      
      // 添加书记员
      this.clerkList.forEach(clerk => {
        participants.push({
          id: `clerk_${clerk.clerkId || clerk.id}`,
          displayName: `书记员`,
          name: clerk.clerkName || clerk.name,
          meetingCode: this.shouldShowMeetingCode ? (clerk.meetingCode || clerk.meetingNumber || clerk.meeting_number || 'xxxx') : undefined
        });
      });
      
      return participants;
    }
  },
  mounted() {
    console.log('NewHearing组件已挂载，context:', this.context, 'isEdit:', this.isEdit);
    
    // 获取互联网仲裁庭选项（所有场景都需要）
    this.fetchCourtOptions();
    
    // 根据场景和模式执行不同的初始化逻辑
    if (this.isEdit) {
      // 编辑模式：加载现有庭审数据
      this.loadExistingHearingData();
    } else {
      // 新增模式：需要查询当事人、代理人、仲裁员、书记员信息
      // 但不显示会议号
      this.fetchPartyList();
      this.fetchAgentList();
      this.fetchArbitratorList();
      this.fetchClerkList();
    }
  },
  
  beforeDestroy() {
    // 组件销毁前设置标志，防止异步操作继续执行
    this.isComponentMounted = false;
  },
  methods: {
    handleSave() {
      this.$refs.formRef.validate(async valid => {
        if (!valid) return;
        this.saving = true;
        try {
          // 构建请求数据
          const requestData = {
            caseId: this.caseId,
            tenantId: this.getTenantId(), // 获取当前租户ID
            userId: this.getUserId(), // 获取当前用户ID
            hearingTime: this.form.hearingTime,
            duration: this.form.duration,
            hearingType: this.form.hearingType,
            courtId: this.form.courtId,
            smsNotify: this.form.sendSms
          };
          
          if (this.isEdit) {
            // 编辑模式：调用编辑接口
            await request.put(`/api/hearing/edit/${this.hearingId}`, requestData);
            this.$message.success('线上执法信息更新成功');
          } else {
            // 新增模式：调用创建接口
            await request.post('/api/hearing/createHearing', requestData);
            this.$message.success('线上执法创建成功');
          }
          
          // 通知父组件保存成功并关闭弹窗
          this.$emit('saved', this.form);
        } catch (e) {
          console.error('保存失败:', e);
          // 错误消息已经在request拦截器中处理，这里不需要再次显示
        } finally {
          this.saving = false;
        }
      });
    },
    handleCancel() {
      // 通知父组件关闭弹窗
      this.$emit('cancel');
    },
    // 获取当事人列表
    async fetchPartyList() {
      try {
        console.log('=== 开始获取当事人列表 ===');
        console.log('案件ID:', this.caseId);
        console.log('案件ID类型:', typeof this.caseId);
        console.log('案件ID是否为空:', !this.caseId);
        console.log('请求URL:', `/api/party/list?caseId=${this.caseId}`);
        
        // 如果案件ID为空，直接返回
        if (!this.caseId) {
          console.log('案件ID为空，跳过当事人查询');
          this.partyList = [];
          return;
        }
        
        // 调用后端接口获取案件下所有当事人
        const res = await request.get('/api/party/list', { params: { caseId: this.caseId } });
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理当事人列表数据');
          return;
        }
        
        console.log('当事人列表接口返回:', res);
        
        if (res && res.code === 200) {
          this.partyList = (res.data || []).map(p => ({ ...p, meetingCode: 'xxxx' }));
          console.log('当事人列表处理完成:', this.partyList);
        } else {
          console.log('当事人列表为空或查询失败，静默处理');
          this.partyList = [];
        }
      } catch (error) {
        console.log('=== 获取当事人列表出错 ===');
        console.log('错误对象:', error);
        console.log('错误消息:', error.message);
        console.log('错误响应:', error.response);
        console.log('错误状态:', error.response && error.response.status);
        console.log('错误数据:', error.response && error.response.data);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理当事人列表错误');
          return;
        }
        
        console.log('获取当事人列表失败，静默处理');
        this.partyList = [];
      }
    },
    // 获取代理人列表
    async fetchAgentList() {
      try {
        console.log('=== 开始获取代理人列表 ===');
        console.log('案件ID:', this.caseId);
        console.log('案件ID类型:', typeof this.caseId);
        console.log('案件ID是否为空:', !this.caseId);
        console.log('请求URL:', `/api/agent/list/${this.caseId}`);
        
        // 如果案件ID为空，直接返回
        if (!this.caseId) {
          console.log('案件ID为空，跳过代理人查询');
          this.agentList = [];
          return;
        }
        
        const agentRes = await request.get(`/api/agent/list/${this.caseId}`);
        console.log('代理人列表接口返回:', agentRes);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理代理人列表数据');
          return;
        }
        
        if (agentRes && agentRes.code === 200) {
          this.agentList = (agentRes.data || []).map(a => ({ ...a, meetingCode: 'xxxx' }));
          console.log('代理人列表处理完成:', this.agentList);
        } else {
          console.log('代理人列表为空或查询失败，静默处理');
          this.agentList = [];
        }
      } catch (error) {
        console.log('=== 获取代理人列表出错 ===');
        console.log('错误对象:', error);
        console.log('错误消息:', error.message);
        console.log('错误响应:', error.response);
        console.log('错误状态:', error.response && error.response.status);
        console.log('错误数据:', error.response && error.response.data);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理代理人列表错误');
          return;
        }
        
        console.log('获取代理人列表失败，静默处理');
        this.agentList = [];
      }
    },
    
    // 获取仲裁员列表
    async fetchArbitratorList() {
      try {
        console.log('=== 开始获取仲裁员列表 ===');
        console.log('案件ID:', this.caseId);
        
        // 如果案件ID为空，直接返回
        if (!this.caseId) {
          console.log('案件ID为空，跳过仲裁员查询');
          this.arbitratorList = [];
          return;
        }
        
        // 使用新的接口获取包含姓名的仲裁员信息
        const arbitratorRes = await request.get(`/api/arbitrator/listDetail/${this.caseId}`);
        console.log('仲裁员列表接口返回:', arbitratorRes);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理仲裁员列表数据');
          return;
        }
        
        if (arbitratorRes && arbitratorRes.code === 200 && arbitratorRes.data) {
          // 处理返回的仲裁员数据
          this.arbitratorList = arbitratorRes.data.map(arbitrator => ({
            arbitratorId: arbitrator.arbitratorId,
            arbitratorName: arbitrator.arbitratorName || '未知执法员',
            meetingCode: 'xxxx'
          }));
          console.log('仲裁员列表处理完成:', this.arbitratorList);
        } else {
          console.log('仲裁员列表为空或查询失败，静默处理');
          this.arbitratorList = [];
        }
      } catch (error) {
        console.log('=== 获取仲裁员列表出错 ===');
        console.log('错误消息:', error.message);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理仲裁员列表错误');
          return;
        }
        
        console.log('获取仲裁员列表失败，静默处理');
        this.arbitratorList = [];
      }
    },
    
    // 获取书记员列表
    async fetchClerkList() {
      try {
        console.log('=== 开始获取书记员列表 ===');
        console.log('案件ID:', this.caseId);
        console.log('案件ID类型:', typeof this.caseId);
        console.log('案件ID是否为空:', !this.caseId);
        console.log('请求URL:', `/api/user/clerkByCase/${this.caseId}`);
        
        // 如果案件ID为空，直接返回
        if (!this.caseId) {
          console.log('案件ID为空，跳过书记员查询');
          this.clerkList = [];
          return;
        }
        
        // 使用新的书记员查询接口
        const clerkRes = await request.get(`/api/user/clerkByCase/${this.caseId}`);
        console.log('书记员接口返回:', clerkRes);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理书记员列表数据');
          return;
        }
        
        if (clerkRes && clerkRes.code === 200 && clerkRes.data) {
          // 书记员是单个用户，转换为列表格式
          this.clerkList = [{
            ...clerkRes.data,
            clerkId: clerkRes.data.userId,
            clerkName: clerkRes.data.userName,
            meetingCode: 'xxxx'
          }];
          console.log('书记员列表处理完成:', this.clerkList);
        } else {
          console.log('书记员信息为空或查询失败，静默处理');
          this.clerkList = [];
        }
      } catch (error) {
        console.log('=== 获取书记员列表出错 ===');
        console.log('错误对象:', error);
        console.log('错误消息:', error.message);
        console.log('错误响应:', error.response);
        console.log('错误状态:', error.response && error.response.status);
        console.log('错误数据:', error.response && error.response.data);
        
        // 检查组件是否已销毁
        if (!this.isComponentMounted) {
          console.log('组件已销毁，停止处理书记员列表错误');
          return;
        }
        
        console.log('获取书记员列表失败，静默处理');
        this.clerkList = [];
      }
    },
    
    // 从caseHearingParticipantController接口获取庭审参会人信息（案件编辑场景使用）
    async fetchHearingParticipants() {
      try {
        console.log('开始获取庭审参会人信息，庭审ID:', this.hearingId);
        const res = await request.get(`/api/case-hearing-participant/byHearing/${this.hearingId}`);
        console.log('庭审参会人信息接口返回:', res);
        
        if (res && res.code === 200 && res.data) {
          // 将参会人信息转换为统一的格式，包含meeting_number字段
          const participants = res.data.map(p => ({
            id: p.participantId || p.id,
            displayName: this.getParticipantDisplayName(p.participantIdentity),
            name: p.participantName || p.name,
            meetingCode: p.meetingNumber || p.meeting_number || 'xxxx' // 使用meeting_number字段
          }));
          
          // 根据身份分类存储
          this.partyList = participants.filter(p => 
            p.displayName === '申请人' || p.displayName === '被申请人'
          );
          this.agentList = participants.filter(p => 
            p.displayName === '代理人'
          );
          this.arbitratorList = participants.filter(p => 
            p.displayName === '执法员' || p.displayName === '仲裁员'
          );
          this.clerkList = participants.filter(p => 
            p.displayName === '书记员'
          );
          
          console.log('庭审参会人信息处理完成:', { partyList: this.partyList, agentList: this.agentList, arbitratorList: this.arbitratorList, clerkList: this.clerkList });
        } else {
          console.error('庭审参会人信息接口返回错误:', res);
          // 如果接口失败，回退到原有的获取方式
          await this.fetchPartyList();
          await this.fetchAgentList();
          await this.fetchArbitratorList(); // 回退到获取仲裁员
          await this.fetchClerkList(); // 回退到获取书记员
        }
      } catch (error) {
        console.error('获取庭审参会人信息失败:', error);
        // 如果接口失败，回退到原有的获取方式
        await this.fetchPartyList();
        await this.fetchAgentList();
        await this.fetchArbitratorList(); // 回退到获取仲裁员
        await this.fetchClerkList(); // 回退到获取书记员
      }
    },
    
    // 根据参会人身份获取显示名称
    getParticipantDisplayName(participantIdentity) {
      const identityMap = {
        1: '申请人',
        2: '被申请人',
        3: '代理人',
        4: '执法员',
        5: '书记员',
      };
      return identityMap[participantIdentity] || '未知身份';
    },
    // 获取互联网仲裁庭选项列表
    async fetchCourtOptions() {
      try {
        // 调用互联网仲裁庭列表接口，获取所有有效的仲裁庭
        const res = await request.get('/api/virtualCourt/list');
        if (res.code === 200 && res.data && Array.isArray(res.data)) {
          // 转换为下拉选项格式
          this.courtOptions = res.data.map(court => ({
            label: court.name, // 互联网仲裁庭名称
            value: court.courtId // 互联网仲裁庭ID作为值
          }));
          // 如果有互联网仲裁庭，默认选中第一个
          if (this.courtOptions.length > 0) {
            this.form.courtId = this.courtOptions[0].value;
          }
        } else {
          console.error('获取互联网仲裁庭列表失败:', res.message || '返回数据格式错误');
          this.courtOptions = [];
        }
      } catch (error) {
        console.error('调用互联网仲裁庭接口失败:', error);
        // 如果接口调用失败，使用默认选项
        this.courtOptions = [
          { label: '第一执法室', value: 'default1' },
          { label: '第二执法室', value: 'default2' },
          { label: '第三执法室', value: 'default3' }
        ];
        this.form.courtId = 'default1';
      }
    },
    // 加载现有庭审数据
    async loadExistingHearingData() {
      try {
        const res = await request.get(`/api/hearing/getHearingById/${this.hearingId}`);
        if (res.code === 200 && res.data) {
          console.log('加载的庭审数据:', res.data);
          
          // 优先使用hearingExpectedStartTime，如果没有则使用hearingTime
          if (res.data.hearingExpectedStartTime) {
            this.form.hearingTime = res.data.hearingExpectedStartTime;
          } else if (res.data.hearingTime) {
            this.form.hearingTime = res.data.hearingTime;
          }
          
          // 设置开庭时长 - 通过计算开始和结束时间的差值来获取时长
          if (res.data.hearingExpectedStartTime && res.data.hearingExpectedEndTime) {
            const startTime = new Date(res.data.hearingExpectedStartTime);
            const endTime = new Date(res.data.hearingExpectedEndTime);
            // 计算时长（小时），使用Math.round确保精确计算
            const durationInHours = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);
            this.form.duration = Math.round(durationInHours);
          } else {
            console.log('缺少开始时间或结束时间，无法计算时长:', {
              hearingExpectedStartTime: res.data.hearingExpectedStartTime,
              hearingExpectedEndTime: res.data.hearingExpectedEndTime
            });
          }
          
          this.form.courtId = res.data.courtId;
          
          // 设置庭审类型
          if (res.data.hearingType === 3 || res.data.hearingType === 4) {
            this.form.hearingType = res.data.hearingType;
          } else {
            this.form.hearingType = HEARING_TYPE_INVESTIGATION;
          }
          
          // 设置短信通知状态 - 尝试多个可能的字段名
          if (res.data.smsNotify !== undefined) {
            this.form.sendSms = res.data.smsNotify;
          } else if (res.data.sendSms !== undefined) {
            this.form.sendSms = res.data.sendSms;
          } else {
            // 如果后端没有返回短信通知状态，默认保持勾选
            this.form.sendSms = true;
          }
          
          console.log('加载的短信通知状态:', this.form.sendSms);
          
          // 根据场景获取参会人信息
          if (this.isCaseEditContext) {
            // 案件编辑场景：从caseHearingParticipantController接口获取参会人信息
            await this.fetchHearingParticipants();
          } else {
            // 案件列表场景：使用原有的获取方式
            if (res.data.partyList && res.data.partyList.length > 0) {
              this.partyList = res.data.partyList.map(p => ({ ...p, meetingCode: 'xxxx' }));
            } else {
              await this.fetchPartyList();
            }

            if (res.data.agentList && res.data.agentList.length > 0) {
              this.agentList = res.data.agentList.map(a => ({ ...a, meetingCode: 'xxxx' }));
            } else {
              await this.fetchAgentList();
            }
            this.arbitratorList = res.data.arbitratorList || []; // 加载仲裁员
            this.clerkList = res.data.clerkList || []; // 加载书记员
          }
          
          this.submitDate = res.data.submitDate; // 假设庭审数据中包含提交日期
        } else {
          console.error('加载庭审数据失败:', res.message || '返回数据格式错误');
          this.$message.error('加载庭审数据失败，请重试');
        }
      } catch (error) {
        console.error('调用加载庭审数据接口失败:', error);
        this.$message.error('加载庭审数据失败，请重试');
      }
    },
    // 获取当前租户ID
    getTenantId() {
      // 优先从userInfo对象中获取tenantId
      let tenantId = '';
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          tenantId = userInfo.tenantId || '';
        } catch (e) {
          console.error('解析userInfo失败:', e);
        }
      }
      
      // 如果userInfo中没有，再尝试直接获取
      if (!tenantId) {
        tenantId = localStorage.getItem('tenantId') || '';
      }
      
      return tenantId;
    },
    // 获取当前用户ID
    getUserId() {
      // 优先从userInfo对象中获取userId
      let userId = '';
      const userInfoStr = localStorage.getItem('userInfo');
      if (userInfoStr) {
        try {
          const userInfo = JSON.parse(userInfoStr);
          userId = userInfo.userId || '';
        } catch (e) {
          console.error('解析userInfo失败:', e);
        }
      }
      
      // 如果userInfo中没有，再尝试直接获取
      if (!userId) {
        userId = localStorage.getItem('userId') || '';
      }
      
      return userId;
    },
  }
}
</script>

<style scoped>
.new-hearing-dialog {
  padding: 0;
  background-color: #fff;
  width: 100%; /* 确保组件占满弹窗宽度 */
}

.hearing-form {
  margin: 0;
  width: 100%; /* 确保表单占满容器宽度 */
  padding: 10px 12px 0 8px; /* 减少顶部内边距从20px到10px，让布局更紧凑 */
}

.el-input-number {
  width: 150px;
}

.notification-section {
  padding: 12px; /* 减少内边距 */
  margin-top: 6px; /* 减少上边距 */
  margin-bottom: 6px; /* 减少下边距 */
}

.participant-section {
  margin-bottom: 0; /* 只有一个section，不需要底部间距 */
}

.section-title {
  font-weight: 600;
  color: #606266;
  font-size: 14px;
  margin-bottom: 8px; /* 减少标题下方间距 */
  border-bottom: 1px solid #e4e7ed;
  padding-bottom: 4px; /* 减少底部内边距 */
}

.info-text {
  color: #606266; /* 加深文字颜色，提高可读性 */
  font-size: 13px; /* 稍微减小字体 */
  line-height: 1.4; /* 减少行高 */
  margin: 4px 0; /* 减少行间距 */
  padding: 6px 10px; /* 减少内边距 */
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1); /* 添加轻微阴影 */
  word-break: break-all; /* 防止长文本溢出 */
}

.identity {
  font-weight: 600; /* 身份文字加粗 */
  margin-right: 15px; /* 减少身份和名字之间的间距 */
  color: #409eff; /* 身份文字使用主题色 */
}

.name {
  color: #606266; /* 名字文字颜色 */
}

.meeting-code {
  margin-left: 15px; /* 减少会议号和名字之间的间距 */
  color: #909399; /* 会议号文字颜色 */
  font-size: 12px; /* 稍微减小字体 */
}

.action-buttons {
  margin-top: 15px; /* 减少上边距 */
  text-align: right;
  padding: 12px 0; /* 减少上下内边距 */
  border-top: 1px solid #f0f0f0;
}

/* 确保表单项在固定宽度下显示合理 */
:deep(.el-form-item) {
  margin-bottom: 15px; /* 减少表单项之间的间距 */
}

:deep(.el-form-item__content) {
  width: 100%;
}

:deep(.el-date-editor) {
  width: 100% !important;
}

:deep(.el-select) {
  width: 100% !important;
}
</style>